import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trackEvent } from "../utils/analytics";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Autocomplete,
  Badge,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SailRangePicker from "./common/SailRangePicker";
import { SearchOption } from "../utils/mockDataAutoSearch";
import {
  debounce,
  fetchBoatTypes,
  fetchLocationSearch,
  searchCounts,
  searchYachts,
  searchYachtsLocation,
} from "../utils/boatApi";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllData,
  setAmenities,
  setCounts,
  setSearchResultsSlice,
  setYachtLocation,
} from "../store/searchSlice";
import { RootState } from "../store/store";
import { getDefaultDateRange, normalizeDateToUTC } from "../utils/dateUtils";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
// Make sure to create this CSS file

type SearchCardProps = {
  searchtext?: string;
  setLoader?: React.Dispatch<React.SetStateAction<boolean>>;
};

const SearchCard: React.FC<SearchCardProps> = ({ searchtext, setLoader }) => {
  const searchData = useSelector((state: RootState) => state.search);
  const { t } = useTranslation() as any;
  const location = useLocation();
  const navigate = useNavigate();
  // Get the item you passed as state
  const item = location?.state?.location || "";

  const [searchResults, setSearchResults] = useState<SearchOption[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [boatTypes, setBoatTypes] = useState<any>([]);
  const [boatTypeAnchor, setBoatTypeAnchor] = useState<null | HTMLElement>(null);
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [selectedBoatType, setSelectedBoatType] = useState<any>(
    searchData?.selectedBoatType,
  );
  const [selectedLocation, setSelectedLocation] = useState<any | null>("");
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [, setLoadingBoatTypes] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingClickSearch, setLoadingClickSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [treeData, setTreeData] = useState<string | null>(item);
  const dispatch = useDispatch();

  useEffect(() => {
    if (item) {
      setTreeData(item);
    }
  }, [item]);

  useEffect(() => {
    if (treeData) {
      setSelectedLocation(treeData);
    }
  }, [treeData]);

  useEffect(() => {
    const loadBoatTypes = async () => {
      setLoadingBoatTypes(true);
      try {
        const boatTypesData = await fetchBoatTypes();
        setBoatTypes([{ id: 0, label: "All Types" }, ...boatTypesData]);
      } catch (error) {
        // console.error(error);
      } finally {
        setLoadingBoatTypes(false);
      }
    };
    loadBoatTypes();
  }, []);
  const normalizeDate = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  useEffect(() => {

    if (searchData.searchResults?.count > 0 && treeData === "") {
      setSelectedLocation(searchData?.selectedLocation);


      const today = normalizeDate(new Date());

      const [defaultCheckIn, defaultCheckOut] = getDefaultDateRange();

      let validDateRange: [Date, Date] | null = null;

      if (searchData?.checkIn && searchData?.checkOut) {
        const checkInDate = normalizeDate(new Date(searchData.checkIn));
        const checkOutDate = normalizeDate(new Date(searchData.checkOut));
        if (checkInDate < today || checkOutDate < today) {
          validDateRange = [defaultCheckIn, defaultCheckOut];
        } else {
          validDateRange = [checkInDate, checkOutDate];
        }
      }

      setDateRange(validDateRange);

      setSelectedBoatType(
        searchData?.selectedBoatType
          ? searchData?.selectedBoatType
          : boatTypes[0],
      );

      setBoatTypes(searchData?.boatTypes ? searchData?.boatTypes : boatTypes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  useEffect(() => {
    if (boatTypes?.length > 0) {
      setSelectedBoatType(boatTypes[0] || null);
    }
  }, [boatTypes]);

  const locationRequestId = useRef(0);
  const handleLocationSearch = useRef(
    debounce(async (query: string) => {
      if (!query || query.length < 2) {
        return;
      }
      const reqId = ++locationRequestId.current;
      setLoadingLocation(true);
      try {
        setError(null);
        const results = await fetchLocationSearch(query);
        if (reqId !== locationRequestId.current) return; // stale response
        setSearchResults(results);
      } catch {
        if (reqId !== locationRequestId.current) return;
        setError("Failed to fetch locations");
      } finally {
        if (reqId === locationRequestId.current) setLoadingLocation(false);
      }
    }, 500)
  ).current;
  // Reset expanded regions whenever new search results arrive
  useEffect(() => { setExpandedIds(new Set()); }, [searchResults]);

  // Group everything after a sailing_area/island under that item as its "children".
  // This lets the region own both city headers and marina sub-items that follow it.
  const { parentMap, hasChildren } = useMemo(() => {
    const parentMap = new Map<number, string>();
    const hasChildren = new Set<string>();
    let groupKey: string | null = null;
    searchResults.forEach((opt, idx) => {
      if (opt.type === 'sailing_area' || opt.type === 'island' || opt.type === 'city') {
        groupKey = `${opt.type}-${opt.id ?? opt.city ?? opt.name}`;
      } else if (groupKey && opt.isSubItem) {
        parentMap.set(idx, groupKey);
        hasChildren.add(groupKey);
      }
    });
    return { parentMap, hasChildren };
  }, [searchResults]);

  const displayedOptions = useMemo(() =>
    searchResults.filter((opt, idx) => {
      if (opt.type === 'sailing_area' || opt.type === 'island' || opt.type === 'city') return true;
      const pk = parentMap.get(idx);
      if (!pk) return true; // standalone result with no region parent
      return expandedIds.has(pk);
    }),
  [searchResults, parentMap, expandedIds]);

  const optKey = (opt: SearchOption) => `${opt.type}-${opt.id ?? opt.city ?? opt.name}`;

  const hasSearched = useRef(true);
  useEffect(() => {
    if (treeData && searchResults.length > 0) {
      const firstOption = searchResults[0];
      setSelectedLocation(firstOption);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  useEffect(() => {
    if (treeData && selectedLocation.shortName) {
      handleSearch(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const NavigationSearch = useCallback(async (searchtext: string) => {
    if (!searchtext || searchtext.length < 2) {
      return;
    }
    setLoadingLocation(true);
    try {
      setError(null);
      const results = await fetchLocationSearch(searchtext);
      setSearchResults(results);
      if (treeData === "") {
        setSelectedLocation(results[0]);
      }
    } catch {
      setError("Failed to fetch locations");
    } finally {
      setLoadingLocation(false);
      hasSearched.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchtext) {
      NavigationSearch(searchtext);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (searchtext && !hasSearched.current) {
      NavigationSearch(searchtext);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const handleSearch = async (buttonClick: boolean) => {
    if (setLoader) {
      setLoader(true);
    }
    const [checkIn, checkOut] = dateRange || getDefaultDateRange();
    const country = selectedLocation?.shortName || selectedLocation?.code || "";
    const selectedBoatTypeLabel =
      selectedBoatType?.label === "All Types"
        ? ""
        : selectedBoatType?.label || "";
    const city = selectedLocation?.city;
    const id = selectedLocation?.id;
    const type = selectedLocation?.type;

    const normalizedCheckIn = normalizeDateToUTC(checkIn);
    const normalizedCheckOut = normalizeDateToUTC(checkOut);
    if (!country) {
      return;
    }

    try {
      if (buttonClick) {
        setLoadingClickSearch(true);
      }

      const [countResults, YachtsLocation, yachtResults] = await Promise.all([
        searchCounts(
          normalizedCheckIn,
          normalizedCheckOut,
          country,
          "EUR",
          selectedBoatTypeLabel || "",
          0,
          1,
          city,
          id,
          type,
        ),
        searchYachtsLocation(
          normalizedCheckIn,
          normalizedCheckOut,
          country,
          "EUR",
          selectedBoatTypeLabel || "",
          0,
          1,
          city,
          id,
          type,
        ),
        searchYachts(
          normalizedCheckIn,
          normalizedCheckOut,
          country,
          "EUR",
          selectedBoatTypeLabel || "",
          0,
          1,
          city,
          id,
          type,
        ),
      ]);

      if (
        countResults == null ||
        YachtsLocation == null ||
        yachtResults == null
      ) {
        return;
      }

      const data = {
        selectedCheckIn: normalizedCheckIn,
        selectedCheckOut: normalizedCheckOut,
        selectedLocation,
        selectedBoatTypeLabel,
        selectedBoatType,
        boatTypes: boatTypes,
      };

      dispatch(setYachtLocation(YachtsLocation?.response?.yachts || []));
      dispatch(setSearchResultsSlice(yachtResults?.response));
      dispatch(setAllData(data));
      dispatch(setCounts(countResults));
      dispatch(setAmenities(yachtResults?.amenities || []));

      if (buttonClick) {
        navigate("/search-results");
      }
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setLoadingClickSearch(false);
      if (setLoader) {
        setLoader(false);
      }
    }
  };

  const handleSearchClick = async (buttonClick: boolean) => {
    const [checkIn, checkOut] = dateRange || getDefaultDateRange();
    const country = selectedLocation?.shortName || selectedLocation?.code || "";
    // const selectedBoatTypeLabel =
    //   selectedBoatType?.label === "All Types"
    //     ? ""
    //     : selectedBoatType?.label || "";
    const yachtTypes =
      selectedBoatType?.label && selectedBoatType.label !== "All Types"
        ? [selectedBoatType.label]
        : [];
    const city =
      selectedLocation?.type === "base" && selectedLocation?.name
        ? selectedLocation?.name
        : selectedLocation?.city;
    const id = selectedLocation?.id;
    const type = selectedLocation?.type;

    const normalizedCheckIn = normalizeDateToUTC(checkIn);
    const normalizedCheckOut = normalizeDateToUTC(checkOut);
    if (!country) {
      return;
    }

    trackEvent('search_clicked', {
      country,
      boat_type: selectedBoatType?.label || '',
      location_type: type || '',
      city: city || '',
    });

    const searchParams = new URLSearchParams({
      date_from: normalizedCheckIn,
      date_to: normalizedCheckOut,
      country,
      currency: "EUR",
    });

    // Handle different location types properly - same logic as handleNewSearch
    if (type === "base") {
      searchParams.append("base_from_id", id);
    } else if (type === "sailing_area") {
      searchParams.append("sailing_area_id", id);
    } else if (type === "island") {
      searchParams.append("island_name", selectedLocation?.islandName || "");
    } else {
      // For city type, only add id parameter, not city as key
      if (id) searchParams.append("id", id);
    }

    if (city) searchParams.append("city", city);
    if (type) searchParams.append("type", type);

    dispatch(setYachtLocation([]));
    dispatch(setSearchResultsSlice([]));
    dispatch(
      setCounts({
        yacht_types: [],
        cabins: [],
        total_yachts: 0,
        bathrooms: [],
      }),
    );
    dispatch(setAmenities([]));
    if (yachtTypes.length > 0) {
      yachtTypes.forEach((type) => searchParams.append("yacht_types", type));
    }

    window.open(`/search-results?${searchParams.toString()}`, '_blank');

    //         normalizedCheckIn,
    //         normalizedCheckOut,
    //         country,
    //         "EUR",
    //         selectedBoatTypeLabel || "",
    //         beds,
    //         1,
    //         city,
    //         id
  };

  const containerStyles2 = {
    border: "1px solid #d3d3d3",
    borderRadius: "8px",
    py: "4px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    cursor: "pointer",
    width: "100%",
  };

  const containerStyles = {
    border: "1px solid #d3d3d3",
    borderRadius: "8px",
    width: "100%",
  };

  const labelStyles = {
    fontSize: "14px",
    fontWeight: "700",
    color: "#484a4b",
    pt: "0.5rem",
    lineHeight: "0.5rem",
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "center", md: "flex-start" },
          alignItems: "flex-start",
          m: { xs: 0, md: "8rem" },
        }}
      >
        <Card
          sx={{
            py: { xs: 0, sm: 0, md: "2rem" },
            px: { xs: "1rem", sm: "1rem", md: "2rem" },
            borderRadius: "16px",
            boxShadow: { xs: 0, sm: 0, md: "0 4px 20px rgba(0, 0, 0, 0.1)" },
            width: "100%",
            maxWidth: { xs: "100%", sm: "100%", md: "378px" },
          }}
        >
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
              {t("rent_title")}
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: "#5f5f5f" }}
            >
              {t("rent_desc")}{" "}
            </Typography>

            <Box component="form" noValidate autoComplete="off">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={containerStyles}>
                    <Box padding="5px 12px">
                      <Typography sx={labelStyles}>WHERE</Typography>
                      <Autocomplete
                        freeSolo
                        options={displayedOptions}
                        value={selectedLocation}
                        isOptionEqualToValue={(option, value) =>
                          option.countryName === value.countryName ||
                          option.country === value.country
                        }
                        getOptionLabel={(option) => {
                          const part1 = option.name || option.city || "";
                          const part2 =
                            option.countryName || option.country || "";
                          if (typeof option === "string") {
                            return option;
                          }
                          return part1 && part2
                            ? `${part1}, ${part2}`
                            : part1
                              ? part1
                              : part2 || "";
                        }}
                        filterOptions={(options) => options}
                        ListboxProps={{ style: { maxHeight: 380 } }}
                        onInputChange={(event, value, reason) => {
                          if (reason === "input") {
                            setSearchQuery(value);
                            setSearchResults([]);
                            handleLocationSearch(value);
                          }
                        }}
                        onChange={(event, value) => {
                          setSelectedLocation(value);
                          setTreeData("");
                        }}
                        // renderOption={(props, option) => (
                        //   <li {...props}>
                        //     <Box
                        //       sx={{
                        //         display: "flex",
                        //         alignItems: "center",
                        //         width: "100%",
                        //       }}
                        //     >
                        //       <Box sx={{ marginRight: "1rem" }}>
                        //         <img
                        //           src={option.imageUrl}
                        //           alt={option.name}
                        //           style={{
                        //             width: "50px",
                        //             height: "50px",
                        //             borderRadius: "50%",
                        //           }}
                        //         />
                        //       </Box>
                        //       <Box>
                        //         {option.id ? (
                        //           <Typography>
                        //             {option.name},{" "}
                        //             {option.countryName || option.country}
                        //           </Typography>
                        //         ) : (
                        //           <>
                        //             <Typography>
                        //               {option.city},{" "}
                        //               {option.countryName || option.country}
                        //             </Typography>
                        //             <Typography
                        //               variant="body2"
                        //               color="textSecondary"
                        //             >
                        //               {option.countryName || option.country}
                        //             </Typography>
                        //           </>
                        //         )}
                        //       </Box>
                        //       <Badge
                        //         badgeContent={option.yachtCount}
                        //         color="primary"
                        //         sx={{ marginLeft: "auto" }}
                        //         max={10000}
                        //       />
                        //     </Box>
                        //   </li>
                        // )}
                        renderOption={(props, option) => {
                          const TYPE_LABEL: Record<string, string> = {
                            sailing_area: 'region',
                            island: 'island',
                            base: 'marina',
                            city: 'city',
                            country: 'country',
                          };
                          const flagUrl = (option as any).shortName
                            ? `https://flagcdn.com/w40/${(option as any).shortName.toLowerCase()}.png`
                            : null;

                          const bold = (text: string) => {
                            if (!searchQuery || !text) return <>{text}</>;
                            const lo = text.toLowerCase();
                            const qi = lo.indexOf(searchQuery.toLowerCase());
                            if (qi === -1) return <>{text}</>;
                            return <>{text.slice(0, qi)}<strong>{text.slice(qi, qi + searchQuery.length)}</strong>{text.slice(qi + searchQuery.length)}</>;
                          };

                          if (option.isSubItem) {
                            return (
                              <li {...props} key={props.key} style={{ padding: '2px 8px' }}>
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%", pl: 3 }}>
                                  {flagUrl && (
                                    <img src={flagUrl} alt="" style={{ width: 18, height: 12, objectFit: "cover", borderRadius: 2, marginRight: 6, flexShrink: 0, opacity: 0.7 }} />
                                  )}
                                  <Typography variant="caption" sx={{ color: "#666", flexGrow: 1, fontSize: "12px" }}>
                                    ⚓ {bold(option.name || option.city || "")}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#bbb", fontSize: "10px", ml: 1 }}>
                                    marina
                                  </Typography>
                                </Box>
                              </li>
                            );
                          }

                          const label = option.name || option.city || "";
                          const country = option.countryName || "";
                          const typeLabel = option.type ? TYPE_LABEL[option.type] : undefined;
                          const key = optKey(option);
                          const expandable = hasChildren.has(key);
                          const expanded = expandedIds.has(key);

                          return (
                            <li {...props} style={{ padding: '4px 16px 4px 8px' }}>
                              <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                                {flagUrl ? (
                                  <img src={flagUrl} alt={country} style={{ width: 24, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }} />
                                ) : (
                                  <Box sx={{ width: 24, height: 16, bgcolor: "#e0e0e0", borderRadius: 1, flexShrink: 0 }} />
                                )}
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="body2" noWrap sx={{ fontSize: "13px" }}>
                                    {bold(label)}{country ? <>, {bold(country)}</> : null}
                                  </Typography>
                                </Box>
                                {typeLabel && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#999", fontSize: "10px", bgcolor: "#f5f5f5", px: 0.6, py: 0.1, borderRadius: "3px", whiteSpace: "nowrap", flexShrink: 0 }}
                                  >
                                    {typeLabel}
                                  </Typography>
                                )}
                                {expandable && (
                                  <IconButton
                                    size="small"
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setExpandedIds(prev => {
                                        const next = new Set(prev);
                                        if (next.has(key)) next.delete(key); else next.add(key);
                                        return next;
                                      });
                                    }}
                                    sx={{ p: 0.2, color: "#aaa", flexShrink: 0 }}
                                  >
                                    {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                )}
                                <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Badge
                                    badgeContent={option.yachtCount}
                                    color="primary"
                                    max={10000}
                                    sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '17px', height: '17px', position: 'static', transform: 'none' } }}
                                  />
                                </Box>
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="standard"
                            placeholder="Enter a city, region, etc."
                            InputLabelProps={{
                              shrink: true,
                              sx: {
                                fontWeight: "bold",
                                color: "#6d6d6d",
                              },
                            }}
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              sx: {
                                borderRadius: "8px",
                                borderColor: "#ccc",
                                "&:hover": {
                                  borderColor: "#888",
                                },
                              },
                              endAdornment: (
                                <>
                                  {loadingLocation ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{
                          "& .MuiAutocomplete-inputRoot": {
                            borderRadius: "8px",
                          },
                        }}
                      />
                      {error && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ marginTop: "8px" }}
                        >
                          {error}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={containerStyles}>
                    <Box padding="5px 12px">
                      <Grid position="relative" container>
                        <Grid item xs={6}>
                          <Typography sx={labelStyles}>CHECK-IN</Typography>
                        </Grid>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            position: "absolute",
                            top: "20%",
                            left: "50%",
                            height: "40px",
                          }}
                        />
                        <Grid item xs={6}>
                          <Typography sx={labelStyles} pl={2}>
                            CHECK-OUT
                          </Typography>
                        </Grid>
                      </Grid>

                      <div className="responsive-date-picker-container">
                        <SailRangePicker
                          startDate={dateRange ? dateRange[0] : null}
                          endDate={dateRange ? dateRange[1] : null}
                          onChange={(start, end) => setDateRange([start, end])}
                          placeholder={["Check-in", "Check-out"]}
                        />
                      </div>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={containerStyles2}>
                    <Box sx={{ pl: "10px", textAlign: "left", width: "100%" }}>
                      <Typography sx={labelStyles}>BOAT TYPE</Typography>
                      <Typography
                        onClick={(e) => { setBoatTypeAnchor(e.currentTarget); setShowMoreTypes(false); }}
                        sx={{ cursor: "pointer", fontSize: "16px", color: "#555", py: 0.5 }}
                      >
                        {selectedBoatType?.label || "All types"}
                      </Typography>
                      <Menu
                        anchorEl={boatTypeAnchor}
                        open={Boolean(boatTypeAnchor)}
                        onClose={() => setBoatTypeAnchor(null)}
                        disableScrollLock
                      >
                        {[{ id: 0, label: "All Types" }, ...boatTypes.filter((t: any) => t.id !== 0 && ["Sail boat", "Catamaran", "Motor boat"].includes(t.label))].map((type: any) => (
                          <MenuItem key={type.id} onClick={() => { setSelectedBoatType(type.id === 0 ? null : type); setBoatTypeAnchor(null); }}>
                            {type.label}
                          </MenuItem>
                        ))}
                        {!showMoreTypes ? (
                          <MenuItem onClick={() => setShowMoreTypes(true)} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                            Other...
                          </MenuItem>
                        ) : (
                          boatTypes.filter((t: any) => t.id !== 0 && !["Sail boat", "Catamaran", "Motor boat"].includes(t.label)).map((type: any) => (
                            <MenuItem key={type.id} onClick={() => { setSelectedBoatType(type); setBoatTypeAnchor(null); }}>
                              {type.label}
                            </MenuItem>
                          ))
                        )}
                      </Menu>
                    </Box>

                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{
                      backgroundColor: "#f04e23",
                      borderRadius: "8px",
                      height: "100%",
                      fontSize: "1rem",
                    }}
                    startIcon={
                      loadingClickSearch ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={() => handleSearchClick(true)}
                    disabled={loadingClickSearch || !selectedLocation}
                  >
                    {loadingClickSearch ? "Searching..." : "Search"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SearchCard;
